/**
 * Ansökan om andrahandsuthyrning (Formulär R-08).
 *
 * The first-hand tenant's application to the landlord for consent to
 * sublet. Party roles describe the FUTURE andrahandsavtal: the applicants
 * (kontraktsinnehavare) are `lessor` 0/1, the proposed sub-tenant is
 * `lessee` 0. Printed, signed by hand and handed to the landlord — never
 * routed to an e-signing provider.
 *
 * Legal text verbatim from `base-templates/ansökan om
 * andrahandsuthyrning.docx` (Haymanot-approved).
 */
import { em, nodes, option, optionList, p, section, strong, t, val } from '@hyranai/regta-document-ir';
import { dateVal, fieldRows, numVal, partySlot, pickCode, propertySlot, selectSlot, shown, signingNodes, slotText, textVal, ticked, whenOption, } from '../common/index.js';
import { SIGNING_PLACE_DATE_SLOTS, answer, legalMeta, legalSigningBlock, placeDateLine } from '../meddelande_18e/kit.js';
const LEGAL = legalMeta('base-templates/ansökan om andrahandsuthyrning.docx');
const KEY = 'ansokan_andrahand';
/** Legacy contracts stored the code itself, so these options carry no separate `value`. */
const opt = (code, label, summary) => ({ code, label, summary });
export const REASON_OPTIONS = [
    opt('provsambo', 'Provsambo', 'Subletting to try living together with a partner (trial cohabitation).'),
    opt('provjobba', 'Provjobba på annan ort', 'Subletting due to trial employment in another location.'),
    opt('studera', 'Studera på annan ort', 'Subletting due to studies in another location.'),
    opt('utlandsvistelse', 'Längre utlandsvistelse', 'Subletting due to an extended stay abroad.'),
    opt('annat', 'Annat skäl', 'Some other reason for subletting, described in free text.'),
];
const OTHER_REASON = REASON_OPTIONS[4];
export const PAYMENT_OPTIONS = [
    opt('e_faktura', 'E-faktura till boendeapp / Mina sidor', 'Rent invoiced as e-invoice via the housing app / My Pages.'),
    opt('e_post', 'Hyresavi till e-postadress', 'Rent invoice sent to an email address.'),
    opt('pappersfaktura', 'Pappersfaktura (ev. aviavgift tillkommer)', 'Rent invoiced on paper (an invoicing fee may apply).'),
];
export const sokande = {
    ...LEGAL,
    blockKey: `${KEY}.sokande`,
    version: 'v1',
    title: 'Förstahandshyresgäst(er) (sökande)',
    summary: 'The apartment number and the applicant tenant(s): names, personnummer, current address and phone.',
    slots: [
        {
            key: 'apartment_official_number',
            label: 'Lägenhetsnummer',
            type: 'text',
            help: 'T.ex. det officiella lägenhetsnumret (1101 etc.)',
        },
        partySlot('tenant_name', 'Namn (hyresgäst 1)', 'lessor', 0, 'name', { required: true }),
        partySlot('tenant_personnummer', 'Personnummer (hyresgäst 1)', 'lessor', 0, 'legalId', { help: 'PII — ej sökbart' }),
        partySlot('cotenant_name', 'Namn (hyresgäst 2)', 'lessor', 1, 'name'),
        partySlot('cotenant_personnummer', 'Personnummer (hyresgäst 2)', 'lessor', 1, 'legalId', { help: 'PII — ej sökbart' }),
        propertySlot('tenant_address', 'Adress (nuvarande)', 'address', { type: 'textarea', required: true }),
        partySlot('tenant_phone', 'Telefon till er', 'lessor', 0, 'phone'),
    ],
    compile: (input, ctx) => [
        section('forstahandshyresgast-er-sokande', 'Förstahandshyresgäst(er) (sökande)', [
            ...nodes(fieldRows([
                ['Lägenhetsnummer', [textVal(input, 'apartment_official_number')]],
                ['Namn (hyresgäst 1)', [textVal(input, 'tenant_name')]],
                ['Personnummer (hyresgäst 1)', [textVal(input, 'tenant_personnummer')]],
                ...(shown(ctx, !!(slotText(input.cotenant_name) || slotText(input.cotenant_personnummer)))
                    ? [
                        ['Namn (hyresgäst 2)', [textVal(input, 'cotenant_name')]],
                        ['Personnummer (hyresgäst 2)', [textVal(input, 'cotenant_personnummer')]],
                    ]
                    : []),
                ['Adress', [textVal(input, 'tenant_address', 'long')]],
                ['Telefon till er', [textVal(input, 'tenant_phone')]],
            ])),
        ]),
    ],
};
export const skal = {
    ...LEGAL,
    blockKey: `${KEY}.skal-for-uthyrning`,
    version: 'v1',
    title: 'Skäl för uthyrning',
    summary: 'Why the tenant wants to sublet, with the supporting documents that must be enclosed.',
    slots: [
        selectSlot('sublet_reason', 'Skäl för uthyrning', REASON_OPTIONS, {
            required: true,
            help: 'Bifoga relevant underlag: personbevis + sambons hyreskontrakt/köpeavtal, anställningsavtal + bostadsavtal på annan ort, antagningsbesked + bostadsavtal på studieorten, biljetter och resplan, eller annat relevant underlag.',
        }),
        {
            key: 'sublet_reason_other',
            label: 'Annat skäl — beskrivning',
            type: 'textarea',
            visibleWhen: whenOption('sublet_reason', OTHER_REASON),
        },
    ],
    compile: (input, ctx) => {
        const reason = pickCode(REASON_OPTIONS, input.sublet_reason);
        const isOther = reason === 'annat';
        return [
            section('skal-for-uthyrning', 'Skäl för uthyrning', [
                p('Till stöd för ditt behov av att hyra ut i andra hand ska du bifoga relevant underlag (personbevis + sambons hyreskontrakt eller köpeavtal, anställningsavtal + nytt bostadsavtal på annan ort, antagningsbesked + bostadsavtal på studieorten, biljetter och resplan, eller annat relevant underlag). I samtliga fall ska ett upprättat andrahandskontrakt bifogas till ansökan.'),
                optionList([
                    ...REASON_OPTIONS.filter((o) => o.code !== 'annat').map((o) => option(ticked(ctx, reason === o.code), o.label, { code: o.code })),
                    option(ticked(ctx, isOther), [t('Annat skäl: '), val(isOther ? slotText(input.sublet_reason_other) : null, { sourceKey: 'sublet_reason_other', blankWidth: 'long' })], { code: 'annat' }),
                ], { sourceKey: 'sublet_reason', select: 'single' }),
            ]),
        ];
    },
};
export const uthyrningstid = {
    ...LEGAL,
    blockKey: `${KEY}.onskad-uthyrningstid`,
    version: 'v1',
    title: 'Önskad uthyrningstid',
    summary: 'The requested sublet period.',
    slots: [
        { key: 'sublet_from', label: 'Fr.o.m.', type: 'date', required: true },
        { key: 'sublet_to', label: 'T.o.m.', type: 'date', required: true },
    ],
    compile: (input, ctx) => [
        section('onskad-uthyrningstid', 'Önskad uthyrningstid', [
            ...nodes(fieldRows([
                ['Fr.o.m.', [dateVal(ctx, input, 'sublet_from')]],
                ['T.o.m.', [dateVal(ctx, input, 'sublet_to')]],
            ])),
        ]),
    ],
};
export const adressUnderUthyrning = {
    ...LEGAL,
    blockKey: `${KEY}.adress-under-uthyrningstiden`,
    version: 'v1',
    title: 'Adress under uthyrningstiden',
    summary: 'Where the applicant can be reached while the apartment is sublet.',
    slots: [
        {
            key: 'applicant_address_during_sublet',
            label: 'Adress under uthyrningstiden',
            type: 'textarea',
            required: true,
            help: 'Ej nuvarande adress — vart ska post nå er under uthyrningen?',
        },
    ],
    compile: (input) => [
        section('adress-under-uthyrningstiden', 'Adress under uthyrningstiden', [
            p([em('Ej nuvarande adress.')], { variant: 'fine_print' }),
            answer(input, 'applicant_address_during_sublet'),
        ]),
    ],
};
export const hyresbetalning = {
    ...LEGAL,
    blockKey: `${KEY}.hyresbetalning-under-uthyrningen`,
    version: 'v1',
    title: 'Hyresbetalning under uthyrningen',
    summary: 'How rent is invoiced during the sublet: e-invoice, e-mailed avi, or paper invoice to an avi address.',
    slots: [
        selectSlot('payment_method', 'Hyresbetalning under uthyrningen', PAYMENT_OPTIONS, { required: true }),
        {
            key: 'payment_email',
            label: 'E-postadress för hyresavi',
            type: 'text',
            visibleWhen: whenOption('payment_method', PAYMENT_OPTIONS[1]),
        },
        {
            key: 'avi_address',
            label: 'Eventuell aviadress (endast vid pappersfaktura)',
            type: 'textarea',
            visibleWhen: whenOption('payment_method', PAYMENT_OPTIONS[2]),
        },
    ],
    compile: (input, ctx) => {
        const method = pickCode(PAYMENT_OPTIONS, input.payment_method);
        const paper = method === 'pappersfaktura';
        return [
            section('hyresbetalning-under-uthyrningen', 'Hyresbetalning under uthyrningen', nodes(optionList([
                option(ticked(ctx, method === 'e_faktura'), 'E-faktura till boendeapp / Mina sidor', { code: 'e_faktura' }),
                option(ticked(ctx, method === 'e_post'), [
                    t('Hyresavi till e-postadress: '),
                    val(shown(ctx, method === 'e_post') ? slotText(input.payment_email) : null, { sourceKey: 'payment_email', blankWidth: 'long' }),
                ], { code: 'e_post' }),
                option(ticked(ctx, paper), 'Pappersfaktura (information om ev. aviavgift tillkommer)', { code: 'pappersfaktura' }),
            ], { sourceKey: 'payment_method', select: 'single' }), shown(ctx, paper) ? p([em('Eventuell aviadress (endast vid pappersfaktura)')], { variant: 'fine_print', keepWithNext: true }) : null, shown(ctx, paper) ? answer(input, 'avi_address') : null)),
        ];
    },
};
export const andrahandshyresgast = {
    ...LEGAL,
    blockKey: `${KEY}.foreslagen-andrahandshyresgast`,
    version: 'v1',
    title: 'Föreslagen andrahandshyresgäst',
    summary: 'The proposed sub-tenant: identity, contact, a reference and household size.',
    slots: [
        partySlot('subtenant_name', 'Namn', 'lessee', 0, 'name', { required: true }),
        partySlot('subtenant_personnummer', 'Personnummer', 'lessee', 0, 'legalId', { help: 'PII — ej sökbart' }),
        partySlot('subtenant_address', 'Nuvarande adress', 'lessee', 0, 'address'),
        partySlot('subtenant_email', 'E-post', 'lessee', 0, 'email'),
        partySlot('subtenant_phone', 'Telefon', 'lessee', 0, 'phone'),
        { key: 'subtenant_reference', label: 'Referens', type: 'text', help: 'Person hyresvärden kan kontakta för referens.' },
        { key: 'subtenant_household_size', label: 'Antal hushållsmedlemmar', type: 'number', integer: true, min: 1 },
    ],
    compile: (input, ctx) => [
        section('foreslagen-andrahandshyresgast', 'Föreslagen andrahandshyresgäst', [
            ...nodes(fieldRows([
                ['Namn', [textVal(input, 'subtenant_name')]],
                ['Personnummer', [textVal(input, 'subtenant_personnummer')]],
                ['Nuvarande adress', [textVal(input, 'subtenant_address', 'long')]],
                ['E-post', [textVal(input, 'subtenant_email')]],
                ['Telefonnummer', [textVal(input, 'subtenant_phone')]],
                ['Referens', [textVal(input, 'subtenant_reference')]],
                ['Antal hushållsmedlemmar', [numVal(ctx, input, 'subtenant_household_size')]],
            ])),
        ]),
    ],
};
export const forutsattningar = {
    ...LEGAL,
    blockKey: `${KEY}.forutsattningar-for-andrahandsuthyrning`,
    version: 'v1',
    title: 'Förutsättningar för andrahandsuthyrning',
    summary: 'The landlord’s conditions: filing deadline, residence requirement, signed sublease, and the tenant’s continuing liability.',
    slots: [
        {
            key: 'application_deadline_months',
            label: 'Ansökan ska lämnas in senast (månader före uthyrning)',
            type: 'number',
            default: 2,
            min: 1,
            help: 'Hyresvärdens handläggningstid — anger hur många månader före önskad uthyrning ansökan ska vara inne.',
        },
    ],
    compile: (input, ctx) => [
        section('forutsattningar-for-andrahandsuthyrning', 'Förutsättningar för andrahandsuthyrning', [
            p([
                t('Ansökan ska lämnas in senast '),
                numVal(ctx, input, 'application_deadline_months'),
                t(' månad före önskad uthyrning. Obs! Uthyrning utan tillstånd kan leda till att ditt hyresavtal sägs upp med omedelbar verkan!'),
            ]),
            p('Kontraktsinnehavaren ska ha varit skriven på adressen i minst 1 år.'),
            p('Du bifogar ett påskrivet andrahandskontrakt till din ansökan och den föreslagna hyresgästen är någon som kan godtas av din hyresvärd. Om du tar ut en för hög hyra riskerar du uppsägning med omedelbar verkan!'),
            p('Du styrker att du har beaktansvärda skäl för andrahandsuthyrning genom att bilägga relevant underlag till din ansökan.'),
            p([strong('Hyresgästen ansvarar för lägenheten och hyrans betalning under uthyrningen')], { keepWithNext: true }),
            p('Du/ni som kontraktsinnehavare ansvarar för lägenheten enligt både lag och kontraktets bestämmelser gentemot din hyresvärd under tiden den är uthyrd i andrahand. Det innebär att det är du/ni som ansvarar för hyresinbetalningar, eventuella skador i lägenheten samt att inga störningar förekommer. En andrahandshyresgäst kan aldrig överta förstahandshyresgästens kontrakt. Eventuellt samtycke till andrahandsuthyrning gäller endast under den period som följer av beslutet. Vid eventuellt behov av förlängning måste du/ni göra en ny ansökan.'),
        ]),
    ],
};
export const personuppgifter = {
    ...LEGAL,
    blockKey: `${KEY}.samtycke-till-personuppgiftsbehandling`,
    version: 'v1',
    title: 'Samtycke till personuppgiftsbehandling',
    summary: 'How the landlord will process the sub-tenant’s personal data.',
    slots: [],
    compile: () => [
        section('samtycke-till-personuppgiftsbehandling', 'Samtycke till personuppgiftsbehandling', [
            p('Andrahandshyresgästens personuppgifter kommer att behandlas av hyresvärden vid handläggningen av denna ansökan och, vid samtycke till uthyrning, för att administrera andrahandsuthyrningen.'),
        ]),
    ],
};
export const intygande = {
    ...LEGAL,
    blockKey: `${KEY}.intygande`,
    version: 'v1',
    title: 'Intygande',
    summary: 'Both signatories attest on their honour that the information given is correct.',
    slots: [],
    compile: () => [
        p([em('Vi (kontraktsinnehavaren och den föreslagna andrahandshyresgästen) intygar på heder och samvete att lämnade uppgifter är riktiga.')]),
    ],
};
export const underskriftKontraktsinnehavare = legalSigningBlock(LEGAL, {
    blockKey: `${KEY}.underskrift-av-kontraktsinnehavaren`,
    title: 'Underskrift av kontraktsinnehavaren',
    summary: 'Place/date and signature panels for the applicant tenant(s).',
    defaults: {
        ref: 'underskrift-av-kontraktsinnehavaren',
        title: 'Underskrift av kontraktsinnehavaren',
        parties: [
            { label: 'Hyresgäst 1', role: 'lessor', ordinal: 0 },
            { label: 'Eventuell hyresgäst 2', role: 'lessor', ordinal: 1 },
        ],
    },
    prelude: placeDateLine,
    slots: SIGNING_PLACE_DATE_SLOTS,
});
const SUBTENANT_SIGNS = [{ label: 'Andrahandshyresgäst', role: 'lessee', ordinal: 0 }];
/** Like the applicants' block, plus the sub-tenant's consent line under the panel. */
export const underskriftAndrahandshyresgast = {
    ...LEGAL,
    blockKey: `${KEY}.underskrift-av-den-foreslagna-andrahandshyresgasten`,
    version: 'v1',
    title: 'Underskrift av den föreslagna andrahandshyresgästen',
    summary: 'Place/date, the sub-tenant’s signature panel and their data-processing consent.',
    slots: SIGNING_PLACE_DATE_SLOTS,
    compile: (input, ctx) => [
        section('underskrift-av-den-foreslagna-andrahandshyresgasten', 'Underskrift av den föreslagna andrahandshyresgästen', nodes(...placeDateLine(input, ctx), ...signingNodes({ ...input, parties: SUBTENANT_SIGNS }, ctx), p([em('Jag samtycker härmed till personuppgiftsbehandling för administration av uthyrningen.')])), { keepTogether: true }),
    ],
};
export const ANSOKAN_ANDRAHAND_BLOCKS = [
    sokande,
    skal,
    uthyrningstid,
    adressUnderUthyrning,
    hyresbetalning,
    andrahandshyresgast,
    forutsattningar,
    personuppgifter,
    intygande,
    underskriftKontraktsinnehavare,
    underskriftAndrahandshyresgast,
];
//# sourceMappingURL=blocks.js.map